import { matchSorter } from 'match-sorter'
import { useMemo, useRef, useState } from 'react'

import { Checkbox, type CheckboxCheckedState } from '@/components/base/checkbox'
import { Input } from '@/components/base/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/base/popover'
import { Skeleton } from '@/components/base/skeleton'
import { Text } from '@/components/base/text'
import { useI18n } from '@/i18n/i18n-react'
import { TrianglesMini } from '@/icons/triangles-mini'
import { clx } from '@/utils/clx'

export type SalesChannelOption = {
	uuid: string
	name: string
	group?: { uuid: string; name: string }
}

type Group = { uuid: string; name: string; channels: SalesChannelOption[] }

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)

/**
 * Multi-select dropdown for sales channels that understands sales-channel groups: a group row
 * selects/deselects all of its channels at once and renders its channels indented beneath it.
 * Channels without a group are listed on their own. An empty selection means "all channels".
 *
 * Shared between the basket analytics filters and the orders advanced filter.
 */
export function SalesChannelMultiSelect({
	salesChannels,
	value,
	onChange,
	allLabel,
	isLoading = false,
	className
}: {
	salesChannels: SalesChannelOption[]
	value: string[]
	onChange: (uuids: string[]) => void
	/** Label shown on the trigger when nothing is selected (i.e. all channels). */
	allLabel: string
	/** While the channel list is still loading, the dropdown shows skeleton rows instead of options. */
	isLoading?: boolean
	className?: string
}) {
	const { L } = useI18n()
	const BA = L.Analytics.BasketAnalysis
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const triggerRef = useRef<HTMLButtonElement>(null)
	// When this select lives inside a modal drawer/dialog, portal the dropdown into that dialog so it
	// stays within the modal's interactive layer (otherwise its rows become unclickable).
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

	const selected = useMemo(() => new Set(value), [value])

	const filtered = useMemo(() => {
		if (!search) return salesChannels
		return matchSorter(salesChannels, search, { keys: ['name'] })
	}, [salesChannels, search])

	const { groups, ungrouped } = useMemo(() => {
		const groupMap = new Map<string, Group>()
		const loose: SalesChannelOption[] = []
		for (const sc of filtered) {
			if (sc.group) {
				const existing = groupMap.get(sc.group.uuid) ?? {
					uuid: sc.group.uuid,
					name: sc.group.name,
					channels: []
				}
				existing.channels.push(sc)
				groupMap.set(sc.group.uuid, existing)
			} else {
				loose.push(sc)
			}
		}
		// Sort groups alphabetically, each group's channels alphabetically, then the ungrouped
		// channels alphabetically.
		const groups = [...groupMap.values()].sort(byName)
		for (const group of groups) {
			group.channels.sort(byName)
		}
		return { groups, ungrouped: loose.sort(byName) }
	}, [filtered])

	const toggleChannel = (uuid: string) => {
		const next = new Set(selected)
		if (next.has(uuid)) {
			next.delete(uuid)
		} else {
			next.add(uuid)
		}
		onChange([...next])
	}

	const groupChecked = (channels: SalesChannelOption[]): CheckboxCheckedState => {
		const selectedCount = channels.filter(c => selected.has(c.uuid)).length
		if (selectedCount === 0) return false
		if (selectedCount === channels.length) return true
		return 'indeterminate'
	}

	const toggleGroup = (channels: SalesChannelOption[]) => {
		const allSelected = channels.every(c => selected.has(c.uuid))
		const next = new Set(selected)
		for (const channel of channels) {
			if (allSelected) {
				next.delete(channel.uuid)
			} else {
				next.add(channel.uuid)
			}
		}
		onChange([...next])
	}

	const triggerLabel = (() => {
		if (value.length === 0) return allLabel
		if (value.length === 1) {
			return salesChannels.find(sc => sc.uuid === value[0])?.name ?? BA.SalesChannelsSelected({ count: 1 })
		}
		return BA.SalesChannelsSelected({ count: value.length })
	})()

	const hasResults = groups.length > 0 || ungrouped.length > 0

	return (
		<Popover
			open={open}
			onOpenChange={next => {
				if (next) {
					setPortalContainer((triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null)
				} else {
					setSearch('')
				}
				setOpen(next)
			}}
		>
			<PopoverTrigger
				ref={triggerRef}
				className={clx(
					'bg-ui-bg-field shadow-buttons-neutral transition-fg flex h-8 w-full select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 outline-none',
					'txt-compact-small text-ui-fg-base',
					'hover:bg-ui-bg-field-hover',
					'focus-visible:shadow-borders-interactive-with-active data-[state=open]:shadow-borders-interactive-with-active',
					className
				)}
			>
				<span className={clx('truncate', value.length === 0 && 'text-ui-fg-muted')}>{triggerLabel}</span>
				<TrianglesMini className='shrink-0 text-ui-fg-muted' />
			</PopoverTrigger>
			<PopoverContent
				align='start'
				container={portalContainer}
				className='w-[var(--radix-popover-trigger-width)] p-0'
			>
				<div className='border-b border-ui-border-base p-1.5'>
					<Input
						size='small'
						type='search'
						autoFocus
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder={BA.SalesChannelSearchPlaceholder()}
					/>
				</div>
				<div className='max-h-[240px] overflow-y-auto p-1'>
					{isLoading &&
						salesChannels.length === 0 &&
						[...Array(4)].map((_, i) => (
							<div key={i} className='flex items-center gap-x-2 px-2 py-1.5'>
								<Skeleton className='h-[15px] w-[15px] rounded-[3px]' />
								<Skeleton className='h-4 w-32' />
							</div>
						))}
					{groups.map(group => (
						<div key={group.uuid} className='mb-1'>
							<label className='flex cursor-pointer items-center gap-x-2 rounded-[4px] px-2 py-1.5 hover:bg-ui-bg-base-hover'>
								<Checkbox
									checked={groupChecked(group.channels)}
									onCheckedChange={() => toggleGroup(group.channels)}
								/>
								<Text size='small' weight='plus' className='truncate'>
									{group.name}
								</Text>
							</label>
							{group.channels.map(channel => (
								<label
									key={channel.uuid}
									className='flex cursor-pointer items-center gap-x-2 rounded-[4px] py-1 pl-7 pr-2 hover:bg-ui-bg-base-hover'
								>
									<Checkbox
										checked={selected.has(channel.uuid)}
										onCheckedChange={() => toggleChannel(channel.uuid)}
									/>
									<Text size='small' className='truncate'>
										{channel.name}
									</Text>
								</label>
							))}
						</div>
					))}
					{ungrouped.map(channel => (
						<label
							key={channel.uuid}
							className='flex cursor-pointer items-center gap-x-2 rounded-[4px] px-2 py-1 hover:bg-ui-bg-base-hover'
						>
							<Checkbox
								checked={selected.has(channel.uuid)}
								onCheckedChange={() => toggleChannel(channel.uuid)}
							/>
							<Text size='small' className='truncate'>
								{channel.name}
							</Text>
						</label>
					))}
					{!hasResults && !isLoading && (
						<div className='px-2 py-1.5'>
							<Text size='small' className='text-ui-fg-subtle'>
								{L.General.NoResultsTitle()}
							</Text>
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
